import type {IndexCopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {ActionList, ActionMenu, Spinner, Stack} from '@primer/react'
import {useState, useRef, useEffect} from 'react'

import {useFetchCopilotSpaces, useCustomCopilotsEnabled} from '../hooks'
import {useRecentCopilotSpaces} from '../hooks/use-recent-copilot-spaces'

import {COPILOT_SPACES_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {SpaceIcon} from '@primer/octicons-react'

export function SpacesSelectNew({
  openCreateSpaceDialog,
  setPendingSpace,
  clearSpaceSelection,
  activeCopilotSpaceId,
  readOnly,
}: {
  openCreateSpaceDialog: (show: boolean) => void
  setPendingSpace: (space: {id: number; owner: string}) => void
  clearSpaceSelection: () => void
  activeCopilotSpaceId: {id: number; owner: string} | null
  readOnly: boolean
}) {
  const manager = useChatManager()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const loadingStatusRef = useRef<HTMLDivElement>(null)
  const firstActionItemRef = useRef<HTMLLIElement>(null)

  const handleMenuOpen = (value: boolean) => {
    if (value) {
      sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_COPILOT_SPACES', mode: 'immersive'})
    }
    setOpen(value)
  }

  const handleCreateSpace = () => {
    openCreateSpaceDialog(true)
    sendEvent('dotcom_chat.activate', {
      target: 'ATTACHMENT_MENU_COPILOT_SPACES_CREATE_SPACE',
      mode: 'immersive',
    })
  }

  const handleViewAllSpaces = () => {
    navigate(COPILOT_SPACES_PATH)
    sendEvent('dotcom_chat.activate', {
      target: 'ATTACHMENT_MENU_COPILOT_SPACES_VIEW_ALL_SPACES',
      mode: 'immersive',
    })
  }

  const handleSpaceSelect = (space: IndexCopilotSpace) => {
    const isCurrentlySelected =
      activeCopilotSpaceId && space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner

    if (isCurrentlySelected) {
      clearSpaceSelection()
    } else {
      manager.addReference({...space, type: 'copilot-space'}, 'spaces')
      setPendingSpace({id: space.id, owner: space.owner})
      sendEvent('dotcom_chat.activate', {
        target: 'ATTACHMENT_MENU_COPILOT_SPACES_SELECT_SPACE',
        space: `${space.owner}/${space.id}`,
      })
    }
    setOpen(false)
  }

  const {data: sortedSpaces = [], isLoading: isLoadingRecentSpaces} = useRecentCopilotSpaces()
  const {isLoading: isLoadingAllSpaces} = useFetchCopilotSpaces(true)

  const copilotSpacesEnabled = useCustomCopilotsEnabled()

  const isLoading = isLoadingRecentSpaces || isLoadingAllSpaces

  // Focus the loading status when it becomes visible, and focus first action item when loading completes
  useEffect(() => {
    if (isLoading && open && loadingStatusRef.current) {
      loadingStatusRef.current.focus()
    } else if (!isLoading && open && firstActionItemRef.current) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        firstActionItemRef.current?.focus()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [isLoading, open])

  // If readOnly (if a thread exists) hide the menu. We can't yet assign
  // a space to an existing thread.
  if (!copilotSpacesEnabled || readOnly) {
    return
  }

  const menuWidth = '260px'
  return (
    <>
      <ActionMenu open={open} onOpenChange={handleMenuOpen}>
        <ActionMenu.Anchor>
          <ActionList.Item
            onSelect={() => {
              sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_COPILOT_SPACES', mode: 'immersive'})
            }}
          >
            <ActionList.LeadingVisual>
              <SpaceIcon />
            </ActionList.LeadingVisual>
            Spaces&hellip;
          </ActionList.Item>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay style={sortedSpaces.length > 0 ? {width: menuWidth} : {}}>
          {isLoading ? (
            <Stack direction="horizontal" align="center" justify="center" padding="normal" style={{width: menuWidth}}>
              <div ref={loadingStatusRef} role="status" className="sr-only" tabIndex={-1}>
                Loading spaces
              </div>
              <Spinner />
            </Stack>
          ) : (
            <ActionList selectionVariant="single" role="menu" aria-label="Select a space">
              {sortedSpaces.length > 0 ? (
                <>
                  <ActionList.Group>
                    <ActionList.GroupHeading aria-label="Recent spaces">Recent</ActionList.GroupHeading>
                    {sortedSpaces.map((space, index) => {
                      const isSelected =
                        !!activeCopilotSpaceId &&
                        space.id === activeCopilotSpaceId.id &&
                        space.owner === activeCopilotSpaceId.owner
                      return (
                        <ActionList.Item
                          ref={index === 0 ? firstActionItemRef : undefined}
                          role="menuitemradio"
                          key={`${space.owner}, ${space.id}`}
                          onSelect={() => handleSpaceSelect(space)}
                          selected={isSelected}
                          aria-checked={isSelected}
                        >
                          <span>{space.name}</span>
                        </ActionList.Item>
                      )
                    })}
                  </ActionList.Group>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleViewAllSpaces}>View all spaces</ActionList.Item>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleCreateSpace}>Create a space</ActionList.Item>
                </>
              ) : (
                <ActionList.Item ref={firstActionItemRef} onSelect={handleCreateSpace}>
                  Create your first space
                </ActionList.Item>
              )}
            </ActionList>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

try{ SpacesSelectNew.displayName ||= 'SpacesSelectNew' } catch {}